import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import store, { RootState } from "@src/reducers/store";
import { loadGatewaysMachinesPageData } from "@src/cloud-connect/gateways-with-machines/use-cases/load-gateways-machines-data";
import { MachinesListSupportSessionsZitadelRoleTypes } from "@common/types/machines-list-types";
import { MachinesListCompanyEntry } from "@src/types/gateways-with-machines";
import {
  setAllMachinesWithGatewaysListData,
  setMachinesWithGatewaysSearchTerms,
  setMachinesWithGatewaysSearchQuery,
  setSearchMachinesWithGatewaysListData,
} from "@src/reducers/gateways-machines-slice";
import { getVPNSupportSessionsUseCase } from "@src/cloud-connect/vpn-manager/use-cases/vpn-get-support-session-use-case";
import { machinesMergeSupportSessionsListData } from "@src/cloud-connect/utils/machines-merge-support-sessions-list-data";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
export const useMachinesListPageHook = () => {
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();

  const authContext = useSelector(
    (s: RootState) => s.yourSlice.authContext,
    shallowEqual,
  );
  const error = useSelector((s: RootState) => s.gatewaysMachinesSlice.error);

  const organization = authContext?.organization;

  const allMachinesListData = useSelector(
    (s: RootState) => s.gatewaysMachinesSlice.allMachinesListData,
    shallowEqual,
  );
  const searchMachinesListData = useSelector(
    (s: RootState) => s.gatewaysMachinesSlice.searchMachinesListData,
  );
  const machinesManagerSearchTerms = useSelector(
    (s: RootState) => s.gatewaysMachinesSlice.machinesManagerSearchTerms,
    shallowEqual,
  );
  const machinesManagerSearchQuery = useSelector(
    (s: RootState) => s.gatewaysMachinesSlice.machinesManagerSearchQuery,
  );
  const machinesManagerShowSearchBar = useSelector(
    (s: RootState) => s.gatewaysMachinesSlice.machinesManagerShowSearchBar,
  );

  const hasActiveSearch = machinesManagerSearchTerms.length > 0;
  const hasSearchResults = Object.keys(searchMachinesListData).length > 0;
  const showNoSearchResults = hasActiveSearch && !hasSearchResults;

  const showSearchMachinesOrAllMachinesListData = hasActiveSearch
    ? searchMachinesListData
    : allMachinesListData;

  useEffect(() => {
    return () => {
      dispatch(setMachinesWithGatewaysSearchTerms([]));
      dispatch(setMachinesWithGatewaysSearchQuery(""));
      dispatch(setSearchMachinesWithGatewaysListData({}));
    };
  }, [dispatch]);

  const hasMachinesPermissions = useMemo(() => {
    const allPermissions = authContext?.permissions ?? [];
    return (
      allPermissions.includes(
        MachinesListSupportSessionsZitadelRoleTypes.ACCEPT_VPN,
      ) ||
      allPermissions.includes(
        MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN,
      ) ||
      allPermissions.includes(
        MachinesListSupportSessionsZitadelRoleTypes.MANAGE_VPN,
      )
    );
  }, [authContext?.permissions]);

  const lastEvent = useSelector(
    (state: RootState) =>
      state.sseSlice.streams?.["service-vpn-manager"]?.["lastEvent"],
  );

  const lastEventAt = useSelector(
    (state: RootState) =>
      state.sseSlice.streams?.["service-vpn-manager"]?.["lastEventAt"],
  );

  const events = useSelector(
    (state: RootState) =>
      state.sseSlice.streams?.["service-vpn-manager"]?.["events"] ?? {},
  );

  const lastProcessedEventRef = useRef<number | null>(null);

  const fetchSupportSessions = useCallback(async () => {
    const res = await getVPNSupportSessionsUseCase();

    if (!res.success) {
      errorSnackbar(res.error || "Failed to fetch support sessions.");
      return;
    }

    const currentData =
      store.getState().gatewaysMachinesSlice.allMachinesListData;
    const updatedData = machinesMergeSupportSessionsListData({
      allMachinesListData: currentData,
      supportSessionsPayload: res.data,
      isPollingResponse: true,
    });

    if (updatedData) {
      dispatch(setAllMachinesWithGatewaysListData(updatedData));
    }
  }, [dispatch]);

  useEffect(() => {
    if (!hasMachinesPermissions || !organization || !authContext) return;
    if (lastEvent !== "support_session") return;
    if (lastEventAt === lastProcessedEventRef.current) return;

    const machineListEntries = Object.values(
      allMachinesListData,
    ) as MachinesListCompanyEntry[];
    const machinesWithCompanies = machineListEntries[0];
    if (!machinesWithCompanies || !machinesWithCompanies.machines) {
      return;
    }

    lastProcessedEventRef.current = lastEventAt;
    fetchSupportSessions();
  }, [
    lastEvent,
    lastEventAt,
    events,
    hasMachinesPermissions,
    organization,
    fetchSupportSessions,
    authContext,
    allMachinesListData,
  ]);

  useEffect(() => {
    if (!authContext) {
      setLoading(false);
      return;
    }

    if (!organization) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      await loadGatewaysMachinesPageData();

      if (hasMachinesPermissions) {
        await fetchSupportSessions();
      }

      setLoading(false);
    };

    fetchData();
  }, [authContext, organization, hasMachinesPermissions, fetchSupportSessions]);

  const handleSearchResults = useCallback(
    (result: Record<string, MachinesListCompanyEntry>) => {
      dispatch(setSearchMachinesWithGatewaysListData(result));
    },
    [dispatch],
  );

  const handleSearchTermsChange = useCallback(
    (machinesManagerSearchTerms: string[]) => {
      dispatch(setMachinesWithGatewaysSearchTerms(machinesManagerSearchTerms));
    },
    [dispatch],
  );

  const handleSearchQueryChange = useCallback(
    (machinesManagerSearchQuery: string) => {
      dispatch(setMachinesWithGatewaysSearchQuery(machinesManagerSearchQuery));
    },
    [dispatch],
  );

  return {
    loading,
    error,
    allMachinesListData,
    searchMachinesListData,
    showSearchMachinesOrAllMachinesListData,
    showNoSearchResults,
    machinesManagerSearchTerms,
    machinesManagerSearchQuery,
    machinesManagerShowSearchBar,
    handleSearchResults,
    handleSearchTermsChange,
    handleSearchQueryChange,
  };
};
